import { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useEffect } from 'react';
import { device, breakpoints } from '../../../utils/styled-component-mixins';
import useMediaQuery from '../../custom-hooks/useMediaQuery';
import onDOMContentLoaded from '../../../utils/on-dom-content-loaded';
import { useLocationContext } from './LocationContext';
import OfficeCard from './OfficeCard';
import useOnResize from '../../custom-hooks/use-on-resize';

const OfficeList = ({ thankYouMessage }) => {
  const { offices } = useLocationContext();

  const isDesktopLarge = useMediaQuery(device.desktopLg);
  const officeCards = offices.map(OfficeCard);
  const officeSlides = officeCards.map((card, index) => (
    <SwiperSlide key={index}>{card}</SwiperSlide>
  ));

  const observeFields = (element) => {
    if (element?.classList?.contains('mktoFormRow')) {
      const elemToObserve = element;
      let prevClassState = elemToObserve.classList.contains('focus-styles');
      // observe for fields, over ride marketo if it is a select
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          // mutation could be a class mutation or a data-wrapper-for mutation. need both.
          // This is needed for blur when marketo tries to pull the styles
          if (mutation.attributeName === 'class') {
            const currentClassState = mutation.target.classList.contains('focus-styles');
            if (prevClassState !== currentClassState) {
              prevClassState = currentClassState;
              const inputField = mutation.target.querySelector('.mktoField');
              // field is select or has value, make sure we keep the styles marketo is removing
              if (
                inputField &&
                (inputField.tagName === 'SELECT' || inputField.classList.contains('has-value'))
              ) {
                mutation.target.classList.add('focus-styles');
              }
            }
          }
          // if the data-wrapper-for has a mutation, it possible that fields were added based on the
          // input of another field because of some dependency. Make sure these get styles if needed.
          else if (mutation.attributeName === 'data-wrapper-for') {
            document.querySelectorAll('.mktoField').forEach((field) => {
              if (field.tagName === 'SELECT') {
                field.parentElement.parentElement.parentElement.classList.add('focus-styles');
              }
            });
          }
        });
      });
      observer.observe(elemToObserve, { attributes: true });
    }
    // runs on first pass using the form. this is how the needed observers are created.
    else if (element?.classList?.contains('mktoForm')) {
      const rows = element.querySelectorAll('.mktoFormRow');
      // create the observers for each row to update the classes when they get mutated
      rows.forEach((formRow) => observeFields(formRow));
    }
  };

  const marketoFormSetup = () => {
    const officeListContainer = document.querySelector('.office-list-container');
    const marketoFormContainer = document.querySelector('.marketo-form-container');
    officeListContainer?.appendChild(marketoFormContainer ?? null);
    marketoFormContainer?.removeAttribute('hidden');

    // set success message
    const successMessage = marketoFormContainer.querySelector('.formSuccessMessage');
    successMessage.innerHTML = thankYouMessage;
  };

  const moveMarketoForm = () => {
    const officeListContainer = document.querySelector('.office-list-container');
    const marketoFormContainer = document.querySelector('.marketo-form-container');
    officeListContainer?.removeChild(marketoFormContainer ?? null);
    if (window.innerWidth > breakpoints.desktopLg) {
      officeListContainer?.appendChild(marketoFormContainer ?? null);
    } else {
      officeListContainer?.prepend(marketoFormContainer ?? null);
    }
  };

  onDOMContentLoaded().then(marketoFormSetup);

  useOnResize(() => {
    moveMarketoForm();
  });

  // need to detect when the form gets added and if it gets moved. Marketo is modifying styles on both.
  useEffect(() => {
    // only run if firefox -others are fine. can be removed if other browsers are having an issue
    // or other browsers can be added.
    if (navigator.userAgent.search('Firefox') > -1) {
      const container = document.querySelector('body');

      // Create a new MutationObserver. need to observe because the form is populated/generated after the document is ready.
      const observer = new MutationObserver((mutationsList) => {
        mutationsList.forEach((mutation) => {
          if (mutation.type === 'childList') {
            // Check if any child nodes were added or removed from the container
            const { addedNodes } = mutation;
            // see if the form was added
            if (addedNodes.length > 0) {
              const form = container.querySelector('.mktoForm');
              if (form) {
                // set up observers on the form
                observeFields(form);
              }
            }
          }
        });
      });

      // Observe changes to the container
      observer.observe(container, { childList: true });

      // Clean up the observer when the component unmounts
      return () => {
        observer.disconnect();
      };
    }
    return null;
  }, []);

  return (
    <div className="office-list-container">
      {isDesktopLarge ? (
        <ul className="office-list">{officeCards}</ul>
      ) : (
        <Swiper
          className="office-slider"
          modules={[Pagination]}
          pagination={{ clickable: false }}
          spaceBetween={16}
          slidesPerView="auto"
          centeredSlides
        >
          {officeSlides}
        </Swiper>
      )}
    </div>
  );
};

export default OfficeList;
